/* ============================================================
   EDA BABY CARE — ANIMATIONS JS
   Safe scroll reveal — content is NEVER hidden if JS fails.
   The class .js-animations-ready is added to <body> only after
   the observer is set up, so opacity:0 never strands content.
   ============================================================ */

'use strict';

/* ----------------------------------------------------------
   SCROLL REVEAL
---------------------------------------------------------- */
function initScrollReveal() {
  // Only run if IntersectionObserver is supported
  if (!('IntersectionObserver' in window)) return;

  const els = document.querySelectorAll('[data-animate]');
  if (!els.length) return;

  // NOW safe to hide elements — observer is about to watch them
  document.body.classList.add('js-animations-ready');

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.08,
    rootMargin: '0px 0px -32px 0px'
  });

  els.forEach(el => observer.observe(el));
}

/* ----------------------------------------------------------
   STAGGERED GRID CHILDREN
   Must run BEFORE ScrollReveal so children are observed too
---------------------------------------------------------- */
function initStaggeredGrids() {
  const grids = document.querySelectorAll(
    '.products-grid, .reviews-grid, .claims-grid, .singles-grid'
  );
  grids.forEach(grid => {
    const children = [...grid.children];
    children.forEach((child, i) => {
      if (!child.hasAttribute('data-animate')) {
        child.setAttribute('data-animate', '');
        if (i < 4) child.setAttribute('data-animate-delay', String(i + 1));
      }
    });
  });
}

/* ----------------------------------------------------------
   VIDEO AUTOPLAY IN VIEWPORT
---------------------------------------------------------- */
function initVideoAutoplay() {
  if (!('IntersectionObserver' in window)) return;
  const videos = document.querySelectorAll('video[autoplay], video[data-autoplay]');
  if (!videos.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const video = entry.target;
      if (entry.isIntersecting) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, { threshold: 0.25 });

  videos.forEach(v => {
    v.muted = true;
    observer.observe(v);
  });
}

/* ----------------------------------------------------------
   VIDEO CARD — tap to play
---------------------------------------------------------- */
function initVideoCards() {
  document.querySelectorAll('.video-card__media').forEach(card => {
    const video = card.querySelector('video');
    if (!video) return;
    const playBtn = card.querySelector('.video-card__play-btn');
    card.addEventListener('click', () => {
      if (video.paused) {
        video.muted = false;
        video.play().catch(() => {});
        if (playBtn) playBtn.style.display = 'none';
      } else {
        video.pause();
        if (playBtn) playBtn.style.display = '';
      }
    });
  });
}

/* ----------------------------------------------------------
   LAZY IMAGE FADE-IN
---------------------------------------------------------- */
function initLazyFade() {
  document.querySelectorAll('img[loading="lazy"]').forEach(img => {
    if (img.complete) return;
    img.style.transition = 'opacity 0.35s ease';
    img.style.opacity = '0';
    img.addEventListener('load', () => { img.style.opacity = '1'; }, { once: true });
  });
}

/* ----------------------------------------------------------
   NAV SCROLL STATE
---------------------------------------------------------- */
function initNavScroll() {
  const header = document.querySelector('.header-outer');
  if (!header) return;
  window.addEventListener('scroll', () => {
    header.classList.toggle('is-scrolled', window.scrollY > 20);
  }, { passive: true });
}

/* ----------------------------------------------------------
   HORIZONTAL DRAG SCROLL (video rail on desktop)
---------------------------------------------------------- */
function initDragScroll() {
  document.querySelectorAll('.video-rail').forEach(rail => {
    let isDown = false, startX, scrollLeft;
    rail.addEventListener('mousedown', e => {
      isDown = true;
      rail.classList.add('is-dragging');
      startX = e.pageX - rail.offsetLeft;
      scrollLeft = rail.scrollLeft;
    });
    rail.addEventListener('mouseleave', () => { isDown = false; rail.classList.remove('is-dragging'); });
    rail.addEventListener('mouseup', () => { isDown = false; rail.classList.remove('is-dragging'); });
    rail.addEventListener('mousemove', e => {
      if (!isDown) return;
      e.preventDefault();
      rail.scrollLeft = scrollLeft - (e.pageX - rail.offsetLeft - startX) * 1.5;
    });
  });
}

/* ----------------------------------------------------------
   HERO PARALLAX (subtle)
---------------------------------------------------------- */
function initHeroParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const hero = document.querySelector('.hero__media img');
  if (!hero) return;
  window.addEventListener('scroll', () => {
    if (window.scrollY < window.innerHeight) {
      hero.style.transform = `translateY(${window.scrollY * 0.12}px)`;
    }
  }, { passive: true });
}

/* ----------------------------------------------------------
   INIT — order matters: grids first, then reveal
---------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  initStaggeredGrids();   // add data-animate to grid children
  initScrollReveal();     // NOW observe all [data-animate] elements
  initVideoAutoplay();
  initVideoCards();
  initLazyFade();
  initNavScroll();
  initDragScroll();
  initHeroParallax();
});
